import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Badge } from './ui/badge';
import {
  Bell, CheckSquare, ClipboardText, Clock, Lightning, Ticket,
  CaretRight, X
} from '@phosphor-icons/react';

const CATEGORY_CONFIG = {
  approval: { icon: CheckSquare, label: 'Approvals', color: 'text-orange-600', bg: 'bg-orange-50' },
  certification: { icon: ClipboardText, label: 'Certifications', color: 'text-teal-600', bg: 'bg-teal-50' },
  expiring: { icon: Clock, label: 'Expiring Access', color: 'text-red-600', bg: 'bg-red-50' },
  jit: { icon: Lightning, label: 'JIT Sessions', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  ticket: { icon: Ticket, label: 'Tickets', color: 'text-purple-600', bg: 'bg-purple-50' },
};

const PRIORITY_DOT = {
  high: 'bg-red-500',
  medium: 'bg-amber-400',
  low: 'bg-slate-300',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = now - date;
  if (diff < 0) {
    // Future date (expiry) — show time until
    const abs = Math.abs(diff);
    const hours = Math.floor(abs / 3600000);
    const mins = Math.floor((abs % 3600000) / 60000);
    if (hours > 24) return `in ${Math.floor(hours / 24)}d`;
    if (hours > 0) return `in ${hours}h`;
    return `in ${mins}m`;
  }
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
}

export default function ActionCenterBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get('/notifications/action-center');
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount + poll every 60s
  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = (item) => {
    setOpen(false);
    navigate(item.link);
  };

  const total = data.total;

  return (
    <div className="relative" ref={ref}>
      {/* Bell Button */}
      <button
        data-testid="action-center-bell"
        onClick={() => { setOpen(!open); if (!open) load(); }}
        className="relative p-2 rounded-md hover:bg-slate-100 transition-colors"
      >
        <Bell size={20} weight={open ? 'fill' : 'regular'} className="text-navy-600" />
        {total > 0 && (
          <span
            data-testid="action-center-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-scale-in"
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          data-testid="action-center-dropdown"
          className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden animate-fade-in"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-navy-900">Action Center</h3>
              {total > 0 && (
                <Badge className="bg-red-100 text-red-700 text-[10px]">{total}</Badge>
              )}
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded">
              <X size={14} className="text-navy-400" />
            </button>
          </div>

          {/* Category Summary */}
          {total > 0 && (
            <div className="flex gap-1 px-4 py-2 border-b border-slate-100 flex-wrap">
              {Object.entries(data.counts).map(([cat, count]) => {
                const cfg = CATEGORY_CONFIG[cat];
                if (!cfg) return null;
                return (
                  <span key={cat} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.bg} ${cfg.color}`}>
                    <cfg.icon size={10} weight="fill" />
                    {count} {cfg.label}
                  </span>
                );
              })}
            </div>
          )}

          {/* Items */}
          <div className="max-h-[380px] overflow-y-auto">
            {total === 0 ? (
              <div className="py-10 text-center">
                <Bell size={32} className="text-slate-200 mx-auto" />
                <p className="text-sm text-navy-400 mt-2">All caught up</p>
                <p className="text-xs text-navy-300 mt-1">No pending actions right now</p>
              </div>
            ) : (
              data.items.map((item) => {
                const cfg = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.approval;
                const Icon = cfg.icon;
                return (
                  <button
                    key={item.id}
                    data-testid={`action-item-${item.id}`}
                    onClick={() => handleClick(item)}
                    className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 last:border-0 group"
                  >
                    {/* Priority dot + Icon */}
                    <div className="relative shrink-0 mt-0.5">
                      <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                        <Icon size={16} weight="duotone" className={cfg.color} />
                      </div>
                      <span className={`absolute -top-0.5 -left-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${PRIORITY_DOT[item.priority] || PRIORITY_DOT.low}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy-900 truncate">{item.title}</p>
                      <p className="text-xs text-navy-500 truncate mt-0.5">{item.subtitle}</p>
                    </div>

                    {/* Time + Arrow */}
                    <div className="shrink-0 text-right flex flex-col items-end gap-1">
                      <span className="text-[10px] text-navy-400">{timeAgo(item.time)}</span>
                      <CaretRight size={12} className="text-navy-300 group-hover:text-orange-500 transition-colors" />
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {total > 0 && (
            <div className="border-t border-slate-100 px-4 py-2">
              <button
                onClick={() => { setOpen(false); navigate('/approvals'); }}
                className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                data-testid="action-center-view-all"
              >
                View all approvals
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
